import React, { useEffect, useRef, useState, useCallback } from "react";
import { Loader2, RotateCw, Volume2, VolumeX, ChevronLeft, ChevronRight } from "lucide-react";
import { loadPannellum } from "./Pano360Viewer";

/**
 * Multi-scene 360° walkthrough tour -- rooms/areas linked by clickable
 * hotspot arrows (Pannellum's native "scene" hotspot type), same as a real
 * estate virtual tour. Built on the same Pannellum engine as the single-
 * image Pano360Viewer (shares its CDN loader), just initialized with a
 * multi-scene tour config instead of one panorama.
 *
 * tour shape (see backend PortfolioIn.tour_360):
 *   {
 *     first_scene: "scene-id",
 *     audio_url: "https://.../narration.mp3" | null,
 *     scenes: [{
 *       id, name, image, pitch, yaw, hfov,
 *       hotspots: [{ pitch, yaw, target_scene_id, text }]
 *     }]
 *   }
 *
 * Usage:
 *   <Tour360Viewer tour={portfolioItem.tour_360} className="w-full h-[420px]" />
 */
export default function Tour360Viewer({ tour, className = "", testId = "tour-360-viewer" }) {
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const audioRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [currentSceneId, setCurrentSceneId] = useState(tour?.first_scene || null);
  const [audioMuted, setAudioMuted] = useState(true);

  const scenes = tour?.scenes || [];
  const hasAudio = !!tour?.audio_url;

  const buildTourConfig = useCallback(() => {
    const sceneMap = {};
    for (const s of scenes) {
      sceneMap[s.id] = {
        title: s.name,
        type: "equirectangular",
        panorama: s.image,
        pitch: s.pitch ?? 0,
        yaw: s.yaw ?? 0,
        hfov: s.hfov ?? 100,
        hotSpots: (s.hotspots || []).map((h) => ({
          pitch: h.pitch,
          yaw: h.yaw,
          type: "scene",
          text: h.text || "Go to next area",
          sceneId: h.target_scene_id,
        })),
      };
    }
    return {
      default: {
        firstScene: tour.first_scene,
        sceneFadeDuration: 800,
        autoLoad: true,
        compass: false,
        showZoomCtrl: true,
        showFullscreenCtrl: true,
        mouseZoom: true,
        touchPanSpeedCoeffFactor: 1,
        minHfov: 50,
        maxHfov: 120,
      },
      scenes: sceneMap,
    };
  }, [tour]);

  useEffect(() => {
    if (!tour?.first_scene || scenes.length === 0 || !containerRef.current) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);

    loadPannellum()
      .then(() => {
        if (cancelled || !containerRef.current || !window.pannellum) return;
        try { viewerRef.current?.destroy?.(); } catch (_e) { /* noop */ }
        viewerRef.current = window.pannellum.viewer(containerRef.current, buildTourConfig());
        viewerRef.current.on("load", () => { if (!cancelled) setLoading(false); });
        viewerRef.current.on("error", (e) => { if (!cancelled) { setErr(String(e)); setLoading(false); } });
        viewerRef.current.on("scenechange", (sceneId) => { if (!cancelled) setCurrentSceneId(sceneId); });
      })
      .catch((e) => { if (!cancelled) { setErr(e.message); setLoading(false); } });

    return () => {
      cancelled = true;
      try { viewerRef.current?.destroy?.(); } catch (_e) { /* noop */ }
      viewerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour?.first_scene, JSON.stringify(scenes)]);

  const jumpToScene = (sceneId) => {
    if (viewerRef.current && sceneId !== currentSceneId) {
      viewerRef.current.loadScene(sceneId);
    }
  };

  const step = (dir) => {
    const idx = scenes.findIndex((s) => s.id === currentSceneId);
    if (idx === -1) return;
    const next = scenes[(idx + dir + scenes.length) % scenes.length];
    jumpToScene(next.id);
  };

  const toggleAudio = () => {
    const el = audioRef.current;
    if (!el) return;
    if (audioMuted) {
      el.muted = false;
      el.play().catch(() => {});
    } else {
      el.muted = true;
    }
    setAudioMuted(!audioMuted);
  };

  if (!tour?.first_scene || scenes.length === 0) return null;

  const currentScene = scenes.find((s) => s.id === currentSceneId) || scenes[0];

  return (
    <div data-testid={testId}>
      <div className={`relative bg-black rounded-lg overflow-hidden ${className}`}
        style={{ WebkitTransform: "translateZ(0)", transform: "translateZ(0)", WebkitBackfaceVisibility: "hidden" }}
      >
        <div ref={containerRef} className="w-full h-full" style={{ touchAction: "none" }} />

        {hasAudio && (
          <audio ref={audioRef} src={tour.audio_url} loop muted={audioMuted} data-testid={`${testId}-audio`} />
        )}

        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60 pointer-events-none z-10">
            <div className="text-center text-white">
              <Loader2 className="w-8 h-8 animate-spin mx-auto" />
              <div className="text-xs mt-2 font-medium opacity-80">Loading tour...</div>
            </div>
          </div>
        )}
        {err && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80 text-white text-sm p-4 text-center z-10">
            <div>
              <div className="font-bold mb-1">Couldn&apos;t load the 360° tour</div>
              <div className="text-xs opacity-70 break-all">{err}</div>
            </div>
          </div>
        )}

        {!loading && !err && (
          <>
            <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur-sm text-white text-[11px] font-bold flex items-center gap-1.5 pointer-events-none z-10">
              <RotateCw className="w-3 h-3" /> {currentScene?.name || "360° · drag to look around"}
            </div>

            {hasAudio && (
              <button
                type="button"
                onClick={toggleAudio}
                data-testid={`${testId}-audio-toggle`}
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/80 z-10"
                title={audioMuted ? "Play narration" : "Mute narration"}
              >
                {audioMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
            )}

            {scenes.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => step(-1)}
                  data-testid={`${testId}-prev`}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/70 z-10"
                  title="Previous area"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={() => step(1)}
                  data-testid={`${testId}-next`}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/70 z-10"
                  title="Next area"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
          </>
        )}
      </div>

      {/* Thumbnail gallery -- every room by name + photo, click to jump straight there */}
      {scenes.length > 1 && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid={`${testId}-gallery`}>
          {scenes.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => jumpToScene(s.id)}
              data-testid={`${testId}-gallery-${s.id}`}
              className={`relative aspect-[4/3] rounded-lg overflow-hidden border-2 transition ${
                s.id === currentSceneId ? "border-[#F7941D]" : "border-transparent hover:border-gray-300"
              }`}
            >
              <img src={s.image} alt={s.name} className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent px-2 pt-4 pb-1.5 text-left">
                <div className="text-white text-[11px] font-bold truncate">{s.name}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
